import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Calendar, 
  Plus, 
  Edit2, 
  Trash2, 
  ExternalLink,
  FileText,
  CheckCircle,
  XCircle,
  Upload,
  File
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '../../config';

const ArrivalSchedule = ({ user }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link_url: '',
    file_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Token available:', !!token);
      const response = await axios.get(`${API}/arrival-schedules`);
      console.log('Response:', response.data);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching arrival schedules:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to load arrival schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/arrival-schedules/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Upload response:', response.data);
      setFormData(prev => ({
        ...prev,
        file_url: response.data.file_url,
        link_url: response.data.file_url
      }));

      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      link_url: '',
      file_url: '',
      is_active: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        link_url: formData.link_url || null,
        file_url: formData.file_url || null,
        is_active: formData.is_active
      };

      if (showEditDialog && selectedSchedule) {
        await axios.put(`${API}/arrival-schedules/${selectedSchedule.id}`, payload);
        toast.success('Arrival schedule updated successfully!');
      } else {
        await axios.post(`${API}/arrival-schedules`, payload);
        toast.success('Arrival schedule created successfully!');
      }

      setShowAddDialog(false);
      setShowEditDialog(false);
      setSelectedSchedule(null);
      resetForm();
      fetchSchedules();
    } catch (error) {
      console.error('Error saving arrival schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to save arrival schedule');
    }
  };

  const handleEdit = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      title: schedule.title || '',
      description: schedule.description || '',
      link_url: schedule.link_url || '',
      file_url: schedule.file_url || '',
      is_active: schedule.is_active !== false
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (schedule) => {
    if (!window.confirm(`Are you sure you want to delete "${schedule.title}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/arrival-schedules/${schedule.id}`);
      toast.success('Arrival schedule deleted successfully!');
      fetchSchedules();
    } catch (error) {
      console.error('Error deleting arrival schedule:', error);
      toast.error('Failed to delete arrival schedule');
    }
  };

  const toggleActive = async (schedule) => {
    try {
      await axios.put(`${API}/arrival-schedules/${schedule.id}`, {
        is_active: !schedule.is_active
      });
      toast.success(`Arrival schedule ${schedule.is_active ? 'deactivated' : 'activated'}!`);
      fetchSchedules();
    } catch (error) {
      console.error('Error toggling arrival schedule:', error);
      toast.error('Failed to update arrival schedule');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="h-7 w-7 text-blue-600" />
            Arrival Schedule
          </h1>
          <p className="text-slate-600 mt-1">
            Manage arrival schedule links and files for the public website
          </p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Arrival Schedule</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., April 2025 Arrivals"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of the schedule"
                />
              </div>

              <div>
                <Label>Upload File (Optional)</Label>
                <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif"
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      {uploading ? 'Uploading...' : 'Click to upload file (PDF, Excel, Images)'}
                    </span>
                  </label>
                </div>
                {formData.file_url && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                    <File className="h-4 w-4" />
                    <span>File uploaded successfully!</span>
                  </div>
                )}
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <div>
                <Label htmlFor="link_url">Paste Link URL (Optional)</Label>
                <Input
                  id="link_url"
                  name="link_url"
                  value={formData.link_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/schedule.pdf"
                  disabled={!!formData.file_url}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter a URL to a file or website. This is disabled when a file is uploaded.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="rounded"
                />
                <Label htmlFor="is_active">Active (visible on website)</Label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => {
                  setShowAddDialog(false);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Create Schedule
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Arrival Schedules
          </CardTitle>
          <CardDescription>
            These schedules will appear on the public website's Arrival Schedule page
          </CardDescription>
        </CardHeader>
        <CardContent>
          {schedules.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No arrival schedules created yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowAddDialog(true)}
              >
                Add your first arrival schedule
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>File/Link</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id} className={!schedule.is_active ? 'bg-slate-50' : ''}>
                    <TableCell>
                      <button
                        onClick={() => toggleActive(schedule)}
                        className="flex items-center"
                      >
                        {schedule.is_active ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-slate-400" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{schedule.title}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-600 max-w-xs truncate">
                        {schedule.description || '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      {schedule.link_url ? (
                        <a 
                          href={schedule.link_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          <FileText className="h-4 w-4" />
                          View File
                        </a>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {schedule.created_at ? new Date(schedule.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(schedule)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(schedule)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Arrival Schedule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit_title">Title</Label>
              <Input
                id="edit_title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., April 2025 Arrivals"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_description">Description (Optional)</Label>
              <Input
                id="edit_description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the schedule"
              />
            </div>

            <div>
              <Label>Upload File (Optional)</Label>
              <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif"
                  className="hidden"
                  id="file-upload-edit"
                />
                <label htmlFor="file-upload-edit" className="cursor-pointer flex flex-col items-center">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {uploading ? 'Uploading...' : 'Click to upload new file (PDF, Excel, Images)'}
                  </span>
                </label>
              </div>
              {formData.file_url && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                  <File className="h-4 w-4" />
                  <span>File uploaded: {formData.file_url.split('/').pop()}</span>
                </div>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>

            <div>
              <Label htmlFor="edit_link_url">Paste Link URL (Optional)</Label>
              <Input
                id="edit_link_url"
                name="link_url"
                value={formData.link_url}
                onChange={handleInputChange}
                placeholder="https://example.com/schedule.pdf"
                disabled={!!formData.file_url}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit_is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="rounded"
              />
              <Label htmlFor="edit_is_active">Active (visible on website)</Label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setShowEditDialog(false);
                setSelectedSchedule(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Update Schedule
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArrivalSchedule;